// Campaign Actions Component
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import EditModal from "@/components/common-components/EditModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Eye, 
  MoreVertical, 
  CheckCircle2, 
  XCircle, 
  Sparkles,
  AlertCircle,
  ArrowRight,
  Play,
  Pause,
  Pencil,
  Trash2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import CampaignStatusChangeDialog from "@/components/business-admin/CampaignStatusChangeDialog";

// All available status options
const ALL_STATUS_OPTIONS = [
  { value: 'CREATED', label: 'Draft' },
  { value: 'IN PROGRESS', label: 'In Progress' },
  { value: 'OUT FOR APPROVAL', label: 'Pending Approval' },
  { value: 'LAUNCHED', label: 'Published' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PAUSED', label: 'Paused' },
];

export default function CampaignActions({ campaign, onApprove, onReject, onStatusChange, onEditCampaignName, onDelete }) {
  const navigate = useNavigate();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Edit campaign name state
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editedCampaignName, setEditedCampaignName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isDeletingCampaign, setIsDeletingCampaign] = useState(false);

  // Get other status options (exclude current status)
  const otherStatusOptions = ALL_STATUS_OPTIONS.filter(status => status.value !== campaign.status);

  const handleApprove = () => {
    onApprove(campaign.campaign_id);
    setShowApproveDialog(false);
  };

  const handleReject = () => {
    onReject(campaign.campaign_id, rejectReason);
    setShowRejectDialog(false);
    setRejectReason("");
  };

  const handleStatusChange = () => {
    if (onStatusChange && selectedNewStatus) {
      onStatusChange(campaign.campaign_id, selectedNewStatus);
      setShowStatusChangeDialog(false);
      setSelectedNewStatus(null);
    }
  };

  const handleStatusSelect = (newStatus) => {
    setSelectedNewStatus(newStatus);
    setShowStatusChangeDialog(true);
  };

  const handleViewDetails = () => {
    navigate(`/business-admin/campaign/${campaign.campaign_id}`);
  };

  const handleEditNameClick = () => {
    setEditedCampaignName(campaign.campaign_name || "");
    setShowEditNameModal(true);
  };

  const handleSaveCampaignName = async () => {
    if (!editedCampaignName.trim()) {
      return; // Validation handled by hook
    }

    try {
      setIsEditingName(true);
      if (onEditCampaignName) {
        await onEditCampaignName(campaign.campaign_id, editedCampaignName);
        // Success toast is shown by the hook
        setShowEditNameModal(false);
      }
    } catch (error) {
      // Error toast is shown by the hook
      console.error("Failed to update campaign name:", error);
    } finally {
      setIsEditingName(false);
    }
  };

  const handleCloseEditModal = () => {
    if (!isEditingName) {
      setShowEditNameModal(false);
      setEditedCampaignName("");
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      try {
        setIsDeletingCampaign(true);
        await onDelete(campaign.campaign_id);
        // Success toast is shown by the hook
        setShowDeleteDialog(false);
      } catch (error) {
        // Error toast is shown by the hook
        console.error("Failed to delete campaign:", error);
        // Keep dialog open on error so user can see what happened and click cancel
      } finally {
        setIsDeletingCampaign(false);
      }
    }
  };

  return (
    <>
      <div className="flex items-center justify-center gap-1">
        {/* Quick View Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleViewDetails}
          title="View campaign details"
          className="h-9 w-9 hover:bg-primary/10 hover:text-primary transition-all"
        >
          <Eye className="h-4 w-4" />
        </Button>

        {/* More Actions Menu */}
        {(campaign.status === "OUT FOR APPROVAL" || onStatusChange) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-9 w-9 hover:bg-primary/10 transition-all"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Campaign Actions
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Approve/Reject Actions (only for OUT FOR APPROVAL) */}
              {campaign.status === "OUT FOR APPROVAL" && (
                <>
                  <DropdownMenuItem 
                    onClick={() => setShowApproveDialog(true)}
                    className="cursor-pointer focus:bg-green-50 dark:focus:bg-green-950/20"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="font-medium">Approve Campaign</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setShowRejectDialog(true)}
                    className="cursor-pointer focus:bg-red-50 dark:focus:bg-red-950/20"
                  >
                    <XCircle className="mr-2 h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="font-medium">Reject Campaign</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              
              {/* Edit Campaign Name */}
              {onEditCampaignName && (
                <>
                  <DropdownMenuItem 
                    onClick={handleEditNameClick}
                    className="cursor-pointer focus:bg-blue-50 dark:focus:bg-blue-950/20"
                  >
                    <Pencil className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium">Edit Campaign Name</span>
                  </DropdownMenuItem>
                </>
              )}
              
              {/* Delete Campaign */}
              {onDelete && (
                <>
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteDialog(true)}
                    className="cursor-pointer focus:bg-red-50 dark:focus:bg-red-950/20"
                  >
                    <Trash2 className="mr-2 h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="font-medium text-red-600 dark:text-red-400">Delete Campaign</span>
                  </DropdownMenuItem>
                </>
              )}
              
              {/* Separator before status change options if edit or delete options exist */}
              {(onEditCampaignName || onDelete) && onStatusChange && otherStatusOptions.length > 0 && (
                <DropdownMenuSeparator />
              )}
              
              {/* Status Change Actions (for all other statuses) */}
              {onStatusChange && otherStatusOptions.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Change Status To:
                  </DropdownMenuLabel>
                  {otherStatusOptions.map((status) => (
                    <DropdownMenuItem 
                      key={status.value}
                      onClick={() => handleStatusSelect(status.value)}
                      className="cursor-pointer focus:bg-blue-50 dark:focus:bg-blue-950/20"
                    >
                      <ArrowRight className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="font-medium">{status.label}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Approve Confirmation Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Approve Campaign
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <p>Are you sure you want to approve this campaign?</p>
              <div className="mt-4 p-3 bg-muted rounded-lg text-left">
                <p className="font-semibold text-foreground text-sm">
                  {campaign.campaign_name}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {campaign.campaign_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs font-medium">{campaign.formatted_budget}</span>
                </div>
              </div>
              <p className="text-sm pt-2">
                This action will launch the campaign and make it live.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApprove}
              className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog with Reason */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Reject Campaign
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <p>Please provide a reason for rejecting this campaign.</p>
              <div className="mt-4 p-3 bg-muted rounded-lg text-left">
                <p className="font-semibold text-foreground text-sm">
                  {campaign.campaign_name}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {campaign.campaign_type}
                  </Badge>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Rejection Reason <span className="text-destructive">*</span>
            </label>
            <Textarea
              placeholder="Enter a detailed reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This reason will be shared with the campaign creator.
            </p>
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel 
              onClick={() => setRejectReason("")}
              className="mt-0"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={!rejectReason.trim()}
              className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 disabled:opacity-50"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Confirmation Dialog */}
      <CampaignStatusChangeDialog
        open={showStatusChangeDialog}
        onOpenChange={(open) => {
          setShowStatusChangeDialog(open);
          if (!open) {
            setSelectedNewStatus(null);
          }
        }}
        campaignName={campaign.campaign_name}
        currentStatusLabel={campaign.status}
        currentStatusBadgeClassName=""
        newStatusLabel={ALL_STATUS_OPTIONS.find(s => s.value === selectedNewStatus)?.label || selectedNewStatus}
        newStatusBadgeClassName="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 font-medium"
        onConfirm={handleStatusChange}
      />

      {/* Edit Campaign Name Modal */}
      <EditModal
        isOpen={showEditNameModal}
        onClose={handleCloseEditModal}
        title="Edit Campaign Name"
        description="Update the campaign name below."
        label="Campaign Name"
        value={editedCampaignName}
        onChange={setEditedCampaignName}
        onSave={handleSaveCampaignName}
        isLoading={isEditingName}
        placeholder="Enter campaign name"
      />

      {/* Delete Campaign Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-center text-xl text-red-600 dark:text-red-400">
              Delete Campaign
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <p className="font-semibold">Are you sure you want to delete this campaign?</p>
              <div className="mt-4 p-3 bg-muted rounded-lg text-left">
                <p className="font-semibold text-foreground text-sm">
                  {campaign.campaign_name}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {campaign.campaign_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs font-medium">{campaign.formatted_budget}</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white dark:bg-gray-950 rounded-lg border border-red-300 dark:border-red-800">
                <p className="text-sm text-gray-800 dark:text-gray-300 font-medium flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                  <span>
                    This action cannot be undone. This will permanently delete the campaign, 
                    all associated POCs, and related data.
                  </span>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel 
              className="mt-0"
              disabled={isDeletingCampaign}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeletingCampaign}
              className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 disabled:opacity-50"
            >
              {isDeletingCampaign ? (
                <>
                  <span className="mr-2">Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
